import { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    SquaresFour, Plus, Trash, Eye, PencilSimple, FileArrowUp,
    Question, Lightbulb, ChartBar, BookOpen, Megaphone, Quotes,
} from "@phosphor-icons/react";

const CATEGORY_ICON = {
    Question: Question, Insight: Lightbulb, Statistic: ChartBar,
    Educational: BookOpen, Announcement: Megaphone, Testimonial: Quotes,
};
const PREVIEW_PRESETS = {
    Question:     { head: "Dating over 50 means…",         body: "the red flags didn't change. Your willingness to ignore them did." },
    Insight:      { head: "Most dating apps want…",        body: "more swiping. HAEVN wants more matching." },
    Statistic:    { head: "68% completion.",               body: "Members complete a HAEVN profile in one sitting. Average app: 19%." },
    Educational:  { head: "How intent works.",             body: "You name what you want. The team curates who can deliver it. No mutual rejection theater." },
    Announcement: { head: "Austin is opening.",            body: "First city. Limited founding cohort. Waitlist priority by intent depth." },
    Testimonial:  { head: "\"I stopped doom-scrolling.\"", body: "Day one. Real conversations followed. — Member, Austin" },
};

/**
 * SocialTemplatesCard — admin UI for the Canva template library.
 *
 * Per spec: AI writes content, Canva templates supply brand presentation.
 * This card is the operator's control panel for that library.
 */
export default function SocialTemplatesCard() {
    const [templates, setTemplates] = useState([]);
    const [meta, setMeta] = useState({ categories: [], alignments: [] });
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);   // template doc | null
    const [previewing, setPreviewing] = useState(null);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/content-studio/social-templates");
                if (!cancelled) {
                    setTemplates(r.data.templates || []);
                    setMeta({
                        categories: r.data.categories || [],
                        alignments: r.data.alignments || [],
                    });
                }
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load templates");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const reload = async () => {
        const r = await api.get("/content-studio/social-templates");
        setTemplates(r.data.templates || []);
    };

    const toggle = async (t) => {
        try {
            await api.post(`/content-studio/social-templates/${t.id}/toggle`);
            toast.success(`${t.name} ${t.active ? "deactivated" : "activated"}`);
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toggle failed");
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/content-studio/social-templates/${t.id}`);
            toast.success("Template deleted");
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    if (loading) return <p className="text-xs text-muted-foreground">Loading templates…</p>;

    const categoriesCovered = new Set(templates.filter(t => t.active).map(t => t.category));
    const missing = (meta.categories || []).filter(c => !categoriesCovered.has(c));

    return (
        <section className="border border-border bg-card rounded-sm p-6 mb-6" data-testid="social-templates-card">
            <header className="mb-4 pb-3 border-b border-border flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2">
                    <SquaresFour size={18} weight="duotone" className="text-primary mt-0.5" />
                    <div>
                        <h3 className="font-display text-lg font-bold leading-tight">Social Templates</h3>
                        <p className="text-xs text-muted-foreground mt-0.5 max-w-[60ch]">
                            Operator-uploaded Canva cards that back every social asset. AI writes the words; templates supply the brand presentation. Upload one per category; rendering picks the right card automatically.
                        </p>
                    </div>
                </div>
                <Button size="sm" className="rounded-sm" onClick={() => setCreateOpen(true)} data-testid="add-template-btn">
                    <Plus size={13} weight="bold" className="mr-1.5" /> Add template
                </Button>
            </header>

            {missing.length > 0 && (
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 mb-4 text-[12px]" data-testid="missing-categories">
                    <p className="text-amber-700 dark:text-amber-300 font-mono uppercase tracking-wider text-[10px] mb-1">
                        Missing categories
                    </p>
                    <p className="text-foreground/80">
                        No active template for: {missing.join(", ")}. Posts in these categories will fall back to the default branded card.
                    </p>
                </div>
            )}

            {templates.length === 0 ? (
                <div className="border border-dashed border-border rounded-sm p-10 text-center" data-testid="templates-empty">
                    <SquaresFour size={26} weight="duotone" className="mx-auto text-primary mb-3" />
                    <p className="font-display text-base font-semibold">No templates yet</p>
                    <p className="text-sm text-muted-foreground mt-1 max-w-[44ch] mx-auto">
                        Upload your first Canva template — 1080×1350 PNG / JPG, branded card with a clear text-overlay zone.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-list">
                    {templates.map(t => (
                        <TemplateCard
                            key={t.id}
                            template={t}
                            apiBase={apiBase}
                            onToggle={() => toggle(t)}
                            onEdit={() => setEditing(t)}
                            onDelete={() => remove(t)}
                            onPreview={() => setPreviewing(t)}
                        />
                    ))}
                </div>
            )}

            <UploadDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                categories={meta.categories}
                alignments={meta.alignments}
                onCreated={async (t) => {
                    setCreateOpen(false);
                    await reload();
                    toast.success(`Template "${t.name}" added`);
                }}
            />
            <EditDialog
                template={editing}
                categories={meta.categories}
                alignments={meta.alignments}
                onOpenChange={(open) => { if (!open) setEditing(null); }}
                onSaved={async () => { setEditing(null); await reload(); }}
            />
            <PreviewDialog
                template={previewing}
                apiBase={apiBase}
                onOpenChange={(open) => { if (!open) setPreviewing(null); }}
            />
        </section>
    );
}

function TemplateCard({ template, apiBase, onToggle, onEdit, onDelete, onPreview }) {
    const Icon = CATEGORY_ICON[template.category] || SquaresFour;
    const inactive = !template.active;
    return (
        <article
            className={`border rounded-sm p-3 bg-background transition-colors ${inactive ? "border-border opacity-60" : "border-border hover:border-primary/40"}`}
            data-testid={`template-${template.id}`}
        >
            <div className="aspect-[4/5] rounded-sm overflow-hidden mb-3 bg-muted/30 border border-border">
                <img
                    src={`${apiBase}/content-studio/social-templates/file/${template.image_path}`}
                    alt={template.name}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={onPreview}
                />
            </div>
            <div className="flex items-center gap-1.5 mb-1">
                <Icon size={12} weight="duotone" className="text-primary" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{template.category}</span>
                {inactive && (
                    <span className="ml-auto font-mono text-[9px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">inactive</span>
                )}
            </div>
            <p className="font-display font-semibold text-sm leading-tight">{template.name}</p>
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                {template.image_width}×{template.image_height} · {template.text_align} · {template.font_hex}
                {template.char_limit ? ` · ${template.char_limit} chars` : ""}
            </p>
            <div className="flex items-center gap-1 mt-2">
                <Button variant="ghost" size="sm" className="rounded-sm h-7 px-2 text-[10px]" onClick={onPreview} data-testid={`preview-${template.id}`}>
                    <Eye size={11} className="mr-1" /> Preview
                </Button>
                <Button variant="ghost" size="sm" className="rounded-sm h-7 px-2 text-[10px]" onClick={onEdit} data-testid={`edit-${template.id}`}>
                    <PencilSimple size={11} className="mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="rounded-sm h-7 px-2 text-[10px]" onClick={onToggle} data-testid={`toggle-${template.id}`}>
                    {template.active ? "Deactivate" : "Activate"}
                </Button>
                <Button variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0 ml-auto text-muted-foreground hover:text-red-600" onClick={onDelete} data-testid={`delete-${template.id}`}>
                    <Trash size={11} />
                </Button>
            </div>
        </article>
    );
}

const DEFAULT_FORM = {
    name: "",
    category: "Question",
    text_align: "center",
    font_hex: "#FFFFFF",
    char_limit: "",
    zone_x: "100",
    zone_y: "500",
    zone_width: "880",
    zone_height: "350",
    active: true,
};

function UploadDialog({ open, onOpenChange, categories, alignments, onCreated }) {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    /* eslint-disable */
    useEffect(() => {
        if (!open) {
            setForm(DEFAULT_FORM);
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
        }
    }, [open]);
    /* eslint-enable */

    const submit = async () => {
        if (!file) { toast.error("Pick a template image"); return; }
        if (!form.name.trim()) { toast.error("Template name required"); return; }
        setBusy(true);
        try {
            const token = localStorage.getItem("haevn:auth_token");
            const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
            const data = new FormData();
            data.append("file", file);
            data.append("name", form.name.trim());
            data.append("category", form.category);
            data.append("text_align", form.text_align);
            data.append("font_hex", form.font_hex);
            if (form.char_limit) data.append("char_limit", form.char_limit);
            data.append("text_zone", JSON.stringify({
                x: Number(form.zone_x) || 0,
                y: Number(form.zone_y) || 0,
                width: Number(form.zone_width) || 0,
                height: Number(form.zone_height) || 0,
            }));
            data.append("active", form.active ? "true" : "false");
            const resp = await fetch(apiBase + "/content-studio/social-templates", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: data,
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `Upload failed (${resp.status})`);
            }
            const t = await resp.json();
            onCreated(t);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-2xl" data-testid="upload-template-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Add Social Template</DialogTitle>
                    <DialogDescription className="text-[12px]">
                        Upload a 1080×1350 Canva card. The text-zone coordinates tell the renderer where to place the AI-generated graphic text.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    <Field label="Template image">
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="upload-template-file"
                            className="block w-full text-[12px] file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-primary file:text-primary-foreground file:font-mono file:text-[10px] file:uppercase file:tracking-wider"
                        />
                        {file && <p className="text-[10px] text-muted-foreground mt-1 font-mono">↳ {file.name} · {Math.round(file.size / 1024)} KB</p>}
                    </Field>
                    <Field label="Template name">
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Question Card" className="rounded-sm h-9" data-testid="tpl-name" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category">
                            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                <SelectTrigger className="rounded-sm h-9" data-testid="tpl-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Alignment">
                            <Select value={form.text_align} onValueChange={v => setForm({ ...form, text_align: v })}>
                                <SelectTrigger className="rounded-sm h-9" data-testid="tpl-align"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {alignments.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Font color (hex)">
                            <Input value={form.font_hex} onChange={e => setForm({ ...form, font_hex: e.target.value })} placeholder="#FFFFFF" className="rounded-sm h-9 font-mono" data-testid="tpl-font-hex" />
                        </Field>
                        <Field label="Char limit (optional)">
                            <Input value={form.char_limit} onChange={e => setForm({ ...form, char_limit: e.target.value.replace(/[^0-9]/g, "") })} placeholder="60" className="rounded-sm h-9" data-testid="tpl-char-limit" />
                        </Field>
                    </div>
                    <Field label="Text zone (pixels — assumes 1080×1350 canvas)">
                        <div className="grid grid-cols-4 gap-2">
                            <Coord label="X" value={form.zone_x} onChange={v => setForm({ ...form, zone_x: v })} testId="tpl-zone-x" />
                            <Coord label="Y" value={form.zone_y} onChange={v => setForm({ ...form, zone_y: v })} testId="tpl-zone-y" />
                            <Coord label="W" value={form.zone_width} onChange={v => setForm({ ...form, zone_width: v })} testId="tpl-zone-w" />
                            <Coord label="H" value={form.zone_height} onChange={v => setForm({ ...form, zone_height: v })} testId="tpl-zone-h" />
                        </div>
                        <p className="text-[10px] text-muted-foreground font-mono mt-1">
                            x={form.zone_x}, y={form.zone_y}, w={form.zone_width}, h={form.zone_height}
                        </p>
                    </Field>
                    <label className="flex items-center gap-2 text-[12px]">
                        <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="tpl-active" />
                        Active (available for rendering)
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button size="sm" className="rounded-sm" onClick={submit} disabled={busy} data-testid="tpl-save">
                        <FileArrowUp size={12} className="mr-1.5" />
                        {busy ? "Uploading…" : "Add template"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditDialog({ template, categories, alignments, onOpenChange, onSaved }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    /* eslint-disable */
    useEffect(() => {
        if (!template) { setForm(null); return; }
        setForm({
            name: template.name || "",
            category: template.category || "Question",
            text_align: template.text_align || "center",
            font_hex: template.font_hex || "#FFFFFF",
            char_limit: template.char_limit ? String(template.char_limit) : "",
            zone_x: String(template.text_zone?.x ?? 0),
            zone_y: String(template.text_zone?.y ?? 0),
            zone_width: String(template.text_zone?.width ?? 1080),
            zone_height: String(template.text_zone?.height ?? 200),
            active: !!template.active,
        });
    }, [template]);
    /* eslint-enable */

    if (!template || !form) return null;

    const save = async () => {
        if (!form.name.trim()) { toast.error("Template name required"); return; }
        setBusy(true);
        try {
            await api.patch(`/content-studio/social-templates/${template.id}`, {
                name: form.name.trim(),
                category: form.category,
                text_align: form.text_align,
                font_hex: form.font_hex,
                char_limit: form.char_limit ? Number(form.char_limit) : null,
                text_zone: {
                    x: Number(form.zone_x) || 0,
                    y: Number(form.zone_y) || 0,
                    width: Number(form.zone_width) || 0,
                    height: Number(form.zone_height) || 0,
                },
                active: form.active,
            });
            toast.success("Template updated");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!template} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-2xl" data-testid="edit-template-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Edit “{template.name}”</DialogTitle>
                    <DialogDescription className="text-[12px]">
                        Adjust placement, alignment, color, and active state. To swap the image, delete and re-upload.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    <Field label="Name">
                        <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" data-testid="edit-tpl-name" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category">
                            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                <SelectTrigger className="rounded-sm h-9" data-testid="edit-tpl-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Alignment">
                            <Select value={form.text_align} onValueChange={v => setForm({ ...form, text_align: v })}>
                                <SelectTrigger className="rounded-sm h-9" data-testid="edit-tpl-align"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {alignments.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Font color (hex)">
                            <Input value={form.font_hex} onChange={e => setForm({ ...form, font_hex: e.target.value })} className="rounded-sm h-9 font-mono" />
                        </Field>
                        <Field label="Char limit (optional)">
                            <Input value={form.char_limit} onChange={e => setForm({ ...form, char_limit: e.target.value.replace(/[^0-9]/g, "") })} className="rounded-sm h-9" />
                        </Field>
                    </div>
                    <Field label="Text zone (pixels)">
                        <div className="grid grid-cols-4 gap-2">
                            <Coord label="X" value={form.zone_x} onChange={v => setForm({ ...form, zone_x: v })} />
                            <Coord label="Y" value={form.zone_y} onChange={v => setForm({ ...form, zone_y: v })} />
                            <Coord label="W" value={form.zone_width} onChange={v => setForm({ ...form, zone_width: v })} />
                            <Coord label="H" value={form.zone_height} onChange={v => setForm({ ...form, zone_height: v })} />
                        </div>
                    </Field>
                    <label className="flex items-center gap-2 text-[12px]">
                        <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
                        Active
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button size="sm" className="rounded-sm" onClick={save} disabled={busy} data-testid="edit-tpl-save">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PreviewDialog({ template, apiBase, onOpenChange }) {
    const [graphicText, setGraphicText] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [handle, setHandle] = useState("@haevnapp");
    const [rendered, setRendered] = useState(null);
    const [busy, setBusy] = useState(false);

    /* eslint-disable */
    useEffect(() => {
        if (template) {
            const preset = PREVIEW_PRESETS[template.category] || {
                head: "Sample headline.",
                body: "Sample body line that explains the headline in a sentence or two.",
            };
            setGraphicText(preset.head);
            setBodyText(preset.body);
            setRendered(null);
        }
    }, [template]);
    /* eslint-enable */

    if (!template) return null;

    const renderPreview = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/content-studio/social-templates/${template.id}/preview`, {
                graphic_text: graphicText,
                body_text: bodyText,
                handle,
            });
            setRendered(r.data.file_name);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!template} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-3xl" data-testid="preview-template-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{template.name} preview</DialogTitle>
                    <DialogDescription className="text-[12px]">
                        Setup → payoff editorial layout. Headline = serif bold. Body = serif regular. Handle = small attribution at the bottom.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <Field label="Headline (setup)">
                            <Input value={graphicText} onChange={e => setGraphicText(e.target.value)} className="rounded-sm h-9" data-testid="preview-text-input" />
                        </Field>
                        <Field label="Body (payoff)">
                            <textarea
                                value={bodyText}
                                onChange={e => setBodyText(e.target.value)}
                                rows={3}
                                className="w-full rounded-sm border border-input bg-background px-3 py-2 text-sm font-display"
                                data-testid="preview-body-input"
                            />
                        </Field>
                        <Field label="Handle">
                            <Input value={handle} onChange={e => setHandle(e.target.value)} className="rounded-sm h-9 font-mono" data-testid="preview-handle-input" />
                        </Field>
                        <Button size="sm" className="rounded-sm" onClick={renderPreview} disabled={busy} data-testid="preview-render-btn">
                            {busy ? "Rendering…" : "Render preview"}
                        </Button>
                        <div className="space-y-1.5 text-[11px] font-mono text-muted-foreground pt-2 border-t border-border">
                            <p>{template.category} · {template.text_align} · {template.font_hex}{template.char_limit ? ` · ${template.char_limit} chars` : ""}</p>
                            <p>zone: x={template.text_zone?.x}, y={template.text_zone?.y}, w={template.text_zone?.width}, h={template.text_zone?.height}</p>
                            <p>canvas: {template.image_width}×{template.image_height}</p>
                        </div>
                    </div>
                    <div className="border border-border rounded-sm overflow-hidden bg-muted/30">
                        <img
                            src={rendered
                                ? `${apiBase}/content-studio/rendered/${rendered}`
                                : `${apiBase}/content-studio/social-templates/file/${template.image_path}`}
                            alt="preview"
                            className="w-full h-auto block"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">{label}</label>
            {children}
        </div>
    );
}

function Coord({ label, value, onChange, testId }) {
    return (
        <div>
            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
            <Input
                value={value}
                onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ""))}
                className="rounded-sm h-8 text-[12px] font-mono"
                data-testid={testId}
            />
        </div>
    );
}
